import {
	cacheExchange,
	CombinedError,
	createClient,
	fetchExchange,
	mapExchange,
	subscriptionExchange,
	Operation,
	makeOperation,
	ClientOptions,
	Exchange,
} from 'urql';
import { RetryExchangeOptions, retryExchange } from '@urql/exchange-retry';
import { createClient as createWSClient } from 'graphql-ws';
import { OperationDefinitionNode } from 'graphql';

declare global {
	interface Window {
		Cypress?: unknown;
	}
}

interface UrqlClient extends Partial<ClientOptions> {
	url: string;
	withSubscriptions?: boolean;
	withRetry?: boolean;
	retryOptions?: RetryExchangeOptions;
	headers?: Record<string, string>;
	includeCredentials?: boolean;
	wsClientOptions?: Record<string, unknown>;
	errorHandler?: (error: CombinedError, operation: Operation, traceId?: string) => void;
	additionalExchanges?: Exchange[];
}

function buildWsClient(wsClientOptions: Record<string, unknown> = {}) {
	if (!wsClientOptions.url) {
		throw new Error('[UrqlClient] Missing GraphQL Subscriptions URL');
	}

	return createWSClient({
		url: wsClientOptions.url as string,
		shouldRetry: () => true,
		...wsClientOptions,
	});
}

let traceId = '';

export function buildUrqlClient({
	url,
	headers,
	withSubscriptions = false,
	withRetry = false,
	retryOptions = {},
	includeCredentials = true,
	errorHandler,
	wsClientOptions = {},
	additionalExchanges = [],
	...restClientOptions
}: UrqlClient) {
	if (!url) {
		throw new Error('[UrqlClient] Missing GraphQL API URL');
	}

	const wsClient = withSubscriptions ? buildWsClient(wsClientOptions) : undefined;

	const exchanges = [
		mapExchange({
			onOperation(operation) {
				const operationName = (operation.query.definitions[0] as OperationDefinitionNode)?.name?.value;
				const devContext =
					process.env.NODE_ENV === 'development' && !window.Cypress && operationName
						? { url: operation.context.url + `?${operationName}` }
						: {};

				return makeOperation(operation.kind, operation, {
					...operation.context,
					...devContext,
				});
			},
			onError(error: CombinedError, operation: Operation) {
				const { message, graphQLErrors = [], networkError } = error;
				const response = error.response as Response;

				if (process.env.NODE_ENV === 'development') {
					console.log('urqlClient - onError', {
						message,
						graphQLErrors,
						networkError,
						response,
					});
				}

				errorHandler?.(error, operation, traceId);
			},
		}),
		cacheExchange,
		withRetry
			? retryExchange({
					maxNumberAttempts: 2,
					retryIf: (error, operation) => {
						const hasErrors = error.graphQLErrors?.length > 0;
						const operationIsOfTypeQuery = operation.kind === 'query';

						return Boolean((hasErrors && operationIsOfTypeQuery) || error.networkError);
					},
					...retryOptions,
				})
			: undefined,
		fetchExchange,
		wsClient
			? subscriptionExchange({
					forwardSubscription(request) {
						const input = { ...request, query: request.query || '' };
						return {
							subscribe(sink) {
								const unsubscribe = wsClient.subscribe(input, sink);
								return { unsubscribe };
							},
						};
					},
				})
			: undefined,
		...additionalExchanges,
	].filter(Boolean) as Exchange[];

	return createClient({
		url,
		requestPolicy: 'network-only',
		exchanges,
		fetch: (...args) =>
			fetch(...args).then((response) => {
				if (response?.headers) {
					traceId = response.headers.get('traceid') || '';
				}

				return response;
			}),
		fetchOptions: () => {
			return {
				// cookie
				credentials: includeCredentials ? 'include' : 'same-origin',
				headers,
			};
		},
		...restClientOptions,
	});
}
